import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { toast } from "sonner";

export interface Fornecedor {
  id: string;
  user_id?: string;
  nome: string;
  cnpj: string | null;
  contato: string;
  telefone: string;
  email: string;
  endereco: string | null;
  cep?: string;
  categoria: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFornecedorData {
  nome: string;
  cnpj?: string;
  contato: string;
  telefone: string;
  email: string;
  endereco?: string;
  cep?: string;
  categoria: string;
  status: string;
}

export function useFornecedores() {
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchFornecedores = async () => {
    if (!user) {
      setFornecedores([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiList<Fornecedor>("fornecedores");
      setFornecedores(data.sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error: any) {
      toast.error("Erro ao carregar fornecedores: " + error.message);
      setFornecedores([]);
    } finally {
      setLoading(false);
    }
  };

  const createFornecedor = async (fornecedorData: CreateFornecedorData) => {
    if (!user) {
      toast.error("Usuario nao autenticado");
      return false;
    }
    try {
      await apiCreate<Fornecedor>("fornecedores", fornecedorData);
      toast.success("Fornecedor criado com sucesso!");
      await fetchFornecedores();
      return true;
    } catch (error: any) {
      toast.error("Erro ao criar fornecedor: " + error.message);
      return false;
    }
  };

  const updateFornecedor = async (id: string, fornecedorData: Partial<CreateFornecedorData>) => {
    try {
      await apiUpdate<Fornecedor>("fornecedores", id, fornecedorData);
      toast.success("Fornecedor atualizado com sucesso!");
      await fetchFornecedores();
      return true;
    } catch (error: any) {
      toast.error("Erro ao atualizar fornecedor: " + error.message);
      return false;
    }
  };

  const deleteFornecedor = async (id: string) => {
    try {
      await apiDelete("fornecedores", id);
      toast.success("Fornecedor excluido com sucesso!");
      await fetchFornecedores();
      return true;
    } catch (error: any) {
      toast.error("Erro ao excluir fornecedor: " + error.message);
      return false;
    }
  };

  useEffect(() => {
    fetchFornecedores();
  }, [user?.id]);

  return { fornecedores, loading, createFornecedor, updateFornecedor, deleteFornecedor, refetch: fetchFornecedores };
}
